import React, { useState, createRef, useEffect, useCallback } from "react";
import "./TimeSeriesPage.css";
import Cropper from "react-easy-crop";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);
const getApiBaseUrl = () => {
  // Always use the direct IP that works
  const apiUrl = 'https://picogram.nemonet.info:4000';
  console.log(`🎯 Using direct API endpoint: ${apiUrl}`);
  return apiUrl;
};
// Enhanced library loading with proper async handling and error recovery
const loadRequiredLibraries = async () => {
  const librariesToLoad = [
    {
      name: 'Pako',
      url: 'https://cdn.jsdelivr.net/npm/pako@2.1.0/dist/pako.min.js',
      check: () => window.pako
    },
    {
      name: 'UTIF',
      urls: [
        'https://cdn.jsdelivr.net/gh/photopea/UTIF.js/UTIF.js',
        'https://cdn.jsdelivr.net/npm/utif@3.1.0/UTIF.js',
        'https://unpkg.com/utif@3.1.0/UTIF.js'
      ],
      check: () => window.UTIF
    },
    {
      name: 'EXIF',
      url: 'https://cdn.jsdelivr.net/npm/exif-js@2.3.0/exif.js',
      check: () => window.EXIF
    }
  ];

  for (const lib of librariesToLoad) {
    if (!lib.check()) {
      console.log(`Loading ${lib.name} library...`);
      
      const urls = lib.urls || [lib.url];
      let loaded = false;
      
      for (const url of urls) {
        try {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = url;
            script.onload = () => {
              console.log(`${lib.name} library loaded successfully from ${url}`);
              loaded = true;
              resolve();
            };
            script.onerror = () => {
              console.warn(`Failed to load ${lib.name} from ${url}`);
              reject(new Error(`Failed to load from ${url}`));
            };
            document.head.appendChild(script);
          });
          break; // Success, stop trying other URLs
        } catch (error) {
          console.warn(`Attempt failed for ${lib.name} from ${url}:`, error);
          if (url === urls[urls.length - 1]) {
            // Last URL failed
            throw new Error(`Failed to load ${lib.name} from all sources`);
          }
        }
      }
    }
  }
  
  console.log('All libraries loaded successfully');
};
// Add these NEW functions after debugTiffStructure and before processTiffFile

// Calculate optimal processing dimensions to prevent memory issues
const calculateOptimalDimensions = (width, height, maxSize = 4096) => {
  const aspectRatio = width / height;
  
  if (width <= maxSize && height <= maxSize) {
    return { width, height, needsResize: false };
  }
  
  let newWidth, newHeight;
  
  if (width > height) {
    newWidth = maxSize;
    newHeight = Math.round(maxSize / aspectRatio);
  } else {
    newHeight = maxSize;
    newWidth = Math.round(maxSize * aspectRatio);
  }
  
  // Ensure dimensions are even numbers (some AI models prefer this)
  newWidth = Math.floor(newWidth / 2) * 2;
  newHeight = Math.floor(newHeight / 2) * 2;
  
  return { 
    width: newWidth, 
    height: newHeight, 
    needsResize: true,
    originalWidth: width,
    originalHeight: height,
    scaleFactor: newWidth / width
  };
};

// Enhanced image compression for large files
const compressImageForAI = async (canvas, format = 'jpeg', maxSizeKB = 10240) => {
  return new Promise((resolve, reject) => {
    let quality = 0.9;
    const attemptCompression = () => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob'));
          return;
        }
        
        const sizeKB = blob.size / 1024;
        console.log(`Compression attempt: ${sizeKB.toFixed(1)}KB at quality ${quality}`);
        
        if (sizeKB <= maxSizeKB || quality <= 0.3) {
          resolve({ blob, finalQuality: quality, finalSize: sizeKB });
        } else {
          quality -= 0.1;
          attemptCompression();
        }
      }, `image/${format}`, quality);
    };
    
    attemptCompression();
  });
};

// Enhanced TIFF processing with memory management
const processLargeTiffFile = async (file) => {
  return new Promise((resolve, reject) => {
    if (!window.UTIF) {
      reject(new Error('UTIF library not loaded'));
      return;
    }
    
    const reader = new FileReader();
    reader.onload = async function(e) {
      try {
        const buffer = e.target.result;
        console.log(`Processing large TIFF: ${file.name}, size: ${(buffer.byteLength / (1024*1024)).toFixed(1)}MB`);
        
        // Decode TIFF with error handling
        let ifds;
        try {
          ifds = window.UTIF.decode(buffer);
          console.log(`TIFF decode successful. Found ${ifds.length} image(s)`);
        } catch (decodeError) {
          console.error('TIFF decode failed:', decodeError);
          throw new Error(`TIFF processing failed: ${decodeError.message}`);
        }
        
        if (!ifds || ifds.length === 0) {
          throw new Error('No image data found in TIFF file');
        }
        
        // Find the best IFD (largest or first valid one)
        let bestIfd = null;
        let maxPixels = 0;
        
        for (let i = 0; i < ifds.length; i++) {
          const ifd = ifds[i];
          let width = ifd.width || ifd.t256;
          let height = ifd.height || ifd.t257;
          
          if (Array.isArray(width)) width = width[0];
          if (Array.isArray(height)) height = height[0];
          
          if (width && height && width > 0 && height > 0) {
            const pixels = width * height;
            if (pixels > maxPixels) {
              maxPixels = pixels;
              bestIfd = ifd;
              bestIfd.width = width;
              bestIfd.height = height;
            }
          }
        }
        
        if (!bestIfd) {
          throw new Error('No valid image dimensions found in TIFF file');
        }
        
        console.log(`Selected IFD with dimensions: ${bestIfd.width}x${bestIfd.height}`);
        
        // Calculate optimal dimensions for processing
        const optimalDims = calculateOptimalDimensions(bestIfd.width, bestIfd.height, 4096);
        console.log('Optimal dimensions:', optimalDims);
        
        // Check for very large images and warn user
        const totalPixels = bestIfd.width * bestIfd.height;
        if (totalPixels > 50 * 1024 * 1024) { // 50 megapixels
          console.warn(`Large image detected: ${(totalPixels / (1024*1024)).toFixed(1)}MP - processing may take time`);
        }
        
        // Decode the image data
        try {
          window.UTIF.decodeImage(buffer, bestIfd);
          console.log('TIFF image decoded successfully');
        } catch (imageDecodeError) {
          console.error('Image decode failed:', imageDecodeError);
          throw new Error(`Failed to decode TIFF image data: ${imageDecodeError.message}`);
        }
        
        // Convert to RGBA
        let rgba;
        try {
          rgba = window.UTIF.toRGBA8(bestIfd);
          if (!rgba || rgba.length === 0) {
            throw new Error('Failed to convert TIFF to RGBA data');
          }
          console.log(`RGBA conversion successful, ${(rgba.length / (1024*1024)).toFixed(1)}MB`);
        } catch (rgbaError) {
          console.error('RGBA conversion failed:', rgbaError);
          throw new Error(`Failed to convert TIFF to displayable format: ${rgbaError.message}`);
        }
        
        // Create canvas with optimal dimensions
        const canvas = document.createElement('canvas');
        canvas.width = optimalDims.width;
        canvas.height = optimalDims.height;
        const ctx = canvas.getContext('2d');
        
        try {
          // Create temporary canvas with original dimensions
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = bestIfd.width;
          tempCanvas.height = bestIfd.height;
          const tempCtx = tempCanvas.getContext('2d');
          
          // Put original image data on temp canvas
          const imageData = tempCtx.createImageData(bestIfd.width, bestIfd.height);
          imageData.data.set(rgba);
          tempCtx.putImageData(imageData, 0, 0);
          
          // Resize to optimal dimensions on main canvas
          ctx.drawImage(tempCanvas, 0, 0, bestIfd.width, bestIfd.height, 
                       0, 0, optimalDims.width, optimalDims.height);
          
          // Clean up temp canvas
          tempCanvas.width = 1;
          tempCanvas.height = 1;
          
          console.log(`Resized from ${bestIfd.width}x${bestIfd.height} to ${optimalDims.width}x${optimalDims.height}`);
          
          // Compress for AI processing
          const compressionResult = await compressImageForAI(canvas, 'jpeg', 10240); // 10MB max
          
          console.log(`Compressed to ${compressionResult.finalSize.toFixed(1)}KB at quality ${compressionResult.finalQuality}`);
          
          const url = URL.createObjectURL(compressionResult.blob);
          
          // Extract metadata
          const metadata = {
            width: optimalDims.width,
            height: optimalDims.height,
            originalWidth: bestIfd.width,
            originalHeight: bestIfd.height,
            wasResized: optimalDims.needsResize,
            scaleFactor: optimalDims.scaleFactor,
            compressionQuality: compressionResult.finalQuality,
            finalSizeKB: compressionResult.finalSize,
            geoTags: {},
            exifData: {},
            fileInfo: {
              name: file.name,
              size: file.size,
              lastModified: file.lastModified,
              compression: bestIfd.t259?.[0] || 'unknown',
              bitsPerSample: bestIfd.t258?.[0] || 'unknown',
              processed: true
            }
          };
          
          // Extract GeoTIFF tags
          if (bestIfd.t33550 || bestIfd.t33922 || bestIfd.t34735 || bestIfd.t34736 || bestIfd.t34737) {
            console.log('Extracting GeoTIFF metadata...');
            const geoTags = [33922, 33550, 34735, 34736, 34737];
            geoTags.forEach(tag => {
              const tagKey = `t${tag}`;
              if (bestIfd[tagKey]) {
                metadata.geoTags[tag] = bestIfd[tagKey];
                console.log(`GeoTag ${tag}:`, bestIfd[tagKey]);
              }
            });
          }
          
          console.log('Large TIFF processing completed successfully');
          resolve({ 
            url, 
            metadata, 
            originalBuffer: buffer,
            compressionInfo: {
              quality: compressionResult.finalQuality,
              sizeKB: compressionResult.finalSize,
              wasCompressed: true
            }
          });
          
        } catch (canvasError) {
          console.error('Canvas processing failed:', canvasError);
          reject(new Error(`Failed to create displayable image: ${canvasError.message}`));
        }
        
      } catch (error) {
        console.error('Large TIFF processing error:', error);
        reject(error);
      }
    };
    
    reader.onerror = (error) => {
      console.error('File reader error:', error);
      reject(new Error('Failed to read TIFF file'));
    };
    
    reader.readAsArrayBuffer(file);
  });
};
// Debug function to analyze TIFF file structure
const debugTiffStructure = (buffer, filename) => {
  try {
    console.log(`=== TIFF Debug Analysis for ${filename} ===`);
    console.log('File size:', buffer.byteLength, 'bytes');
    
    // Check TIFF header
    const view = new DataView(buffer);
    const byte1 = view.getUint8(0);
    const byte2 = view.getUint8(1);
    const magic = view.getUint16(2, byte1 === 0x49); // little endian if first byte is 0x49
    
    console.log('TIFF Header:', {
      byteOrder: byte1 === 0x49 ? 'Little Endian' : 'Big Endian',
      signature: byte1.toString(16) + byte2.toString(16),
      magic: magic,
      isValidTiff: (byte1 === 0x49 && byte2 === 0x49 && magic === 42) || 
                   (byte1 === 0x4D && byte2 === 0x4D && magic === 42)
    });
    
    if (window.UTIF) {
      const ifds = window.UTIF.decode(buffer);
      console.log('UTIF decode result:', ifds.length, 'IFDs found');
      
      ifds.forEach((ifd, index) => {
        console.log(`IFD ${index}:`, {
          width: ifd.width,
          height: ifd.height,
          t256_ImageWidth: ifd.t256,
          t257_ImageLength: ifd.t257,
          t258_BitsPerSample: ifd.t258,
          t259_Compression: ifd.t259,
          t262_PhotometricInterpretation: ifd.t262,
          t273_StripOffsets: ifd.t273 ? 'present' : 'missing',
          t279_StripByteCounts: ifd.t279 ? 'present' : 'missing',
          totalTags: Object.keys(ifd).filter(k => k.startsWith('t')).length
        });
      });
    }
    
    return true;
  } catch (error) {
    console.error('Debug analysis failed:', error);
    return false;
  }
};

// Enhanced TIFF processing function with comprehensive error handling
const processTiffFile = async (file) => {
  return new Promise((resolve, reject) => {
    if (!window.UTIF) {
      reject(new Error('UTIF library not loaded. Please wait for libraries to load and try again.'));
      return;
    }
    
    const reader = new FileReader();
    reader.onload = function(e) {
      try {
        const buffer = e.target.result;
        console.log(`Processing TIFF: ${file.name}, size: ${buffer.byteLength} bytes`);
        
        // Decode TIFF structure
        let ifds;
        try {
          ifds = window.UTIF.decode(buffer);
          console.log(`TIFF decode successful. Found ${ifds.length} image(s)`);
        } catch (decodeError) {
          console.error('TIFF decode failed:', decodeError);
          
          // Provide specific error messages for common issues
          if (decodeError.message.includes('Unknown compression')) {
            const compressionMatch = decodeError.message.match(/Unknown compression (\d+)/);
            const compression = compressionMatch ? compressionMatch[1] : 'unknown';
            throw new Error(`Unsupported TIFF compression format (${compression}). This TIFF uses a compression method not supported by the browser. Try converting to PNG or uncompressed TIFF.`);
          } else if (decodeError.message.includes('not enough data')) {
            throw new Error('TIFF file appears to be corrupted or incomplete.');
          } else {
            throw new Error(`TIFF processing failed: ${decodeError.message}`);
          }
        }
        
        if (!ifds || ifds.length === 0) {
          throw new Error('No image data found in TIFF file');
        }
        
        // Enhanced IFD selection and dimension extraction
        let ifd = null;
        let validIfdIndex = -1;
        
        // Try to find a valid IFD with proper dimensions
        for (let i = 0; i < ifds.length; i++) {
          const currentIfd = ifds[i];
          console.log(`Checking IFD ${i}:`, {
            width: currentIfd.width,
            height: currentIfd.height,
            t256: currentIfd.t256, // ImageWidth tag
            t257: currentIfd.t257, // ImageLength tag
            bitsPerSample: currentIfd.t258,
            compression: currentIfd.t259,
            photometric: currentIfd.t262
          });
          
          // Extract dimensions from different possible sources
          let width = currentIfd.width;
          let height = currentIfd.height;
          
          // Fallback to TIFF tags if direct properties aren't available
          if (!width && currentIfd.t256) {
            width = Array.isArray(currentIfd.t256) ? currentIfd.t256[0] : currentIfd.t256;
          }
          if (!height && currentIfd.t257) {
            height = Array.isArray(currentIfd.t257) ? currentIfd.t257[0] : currentIfd.t257;
          }
          
          // Update the IFD object with extracted dimensions
          if (width && height && width > 0 && height > 0) {
            currentIfd.width = width;
            currentIfd.height = height;
            ifd = currentIfd;
            validIfdIndex = i;
            console.log(`Found valid IFD at index ${i} with dimensions: ${width}x${height}`);
            break;
          }
        }
        
        if (!ifd) {
          // Provide detailed error information
          const ifdSummary = ifds.map((ifd, i) => ({
            index: i,
            width: ifd.width || ifd.t256,
            height: ifd.height || ifd.t257,
            tags: Object.keys(ifd).filter(key => key.startsWith('t')).length
          }));
          console.error('No valid IFD found. IFD summary:', ifdSummary);
          throw new Error(`No valid image dimensions found in TIFF file. Found ${ifds.length} IFD(s) but none contain valid width/height data. This may be a non-standard TIFF format.`);
        }
        
        console.log('Selected TIFF properties:', {
          ifdIndex: validIfdIndex,
          width: ifd.width,
          height: ifd.height,
          bitsPerSample: ifd.t258,
          compression: ifd.t259,
          photometric: ifd.t262,
          samplesPerPixel: ifd.t277,
          hasGeoTags: !!(ifd.t33550 || ifd.t33922)
        });
        
        // Check for very large images that might cause memory issues
        const pixelCount = ifd.width * ifd.height;
        if (pixelCount > 100 * 1024 * 1024) { // 100 megapixels
          console.warn('Large TIFF detected, processing may be slow');
        }
        
        // Validate final dimensions
        if (!ifd.width || !ifd.height || ifd.width <= 0 || ifd.height <= 0) {
          throw new Error(`Invalid final image dimensions: ${ifd.width}x${ifd.height}`);
        }
        
        // Try to decode the image data
        try {
          window.UTIF.decodeImage(buffer, ifd);
          console.log('TIFF image decoded successfully');
        } catch (imageDecodeError) {
          console.error('Image decode failed:', imageDecodeError);
          throw new Error(`Failed to decode TIFF image data: ${imageDecodeError.message}`);
        }
        
        // Convert to RGBA
        let rgba;
        try {
          rgba = window.UTIF.toRGBA8(ifd);
          if (!rgba || rgba.length === 0) {
            throw new Error('Failed to convert TIFF to RGBA data');
          }
          console.log(`RGBA conversion successful, ${rgba.length} bytes`);
        } catch (rgbaError) {
          console.error('RGBA conversion failed:', rgbaError);
          throw new Error(`Failed to convert TIFF to displayable format: ${rgbaError.message}`);
        }
        
        // Extract metadata
        const metadata = {
          width: ifd.width,
          height: ifd.height,
          geoTags: {},
          exifData: {},
          fileInfo: {
            name: file.name,
            size: file.size,
            lastModified: file.lastModified,
            compression: ifd.t259?.[0] || 'unknown',
            bitsPerSample: ifd.t258?.[0] || 'unknown'
          }
        };
        
        // Extract GeoTIFF tags
        if (ifd.t33550 || ifd.t33922 || ifd.t34735 || ifd.t34736 || ifd.t34737) {
          console.log('Extracting GeoTIFF metadata...');
          const geoTags = [33922, 33550, 34735, 34736, 34737];
          geoTags.forEach(tag => {
            const tagKey = `t${tag}`;
            if (ifd[tagKey]) {
              metadata.geoTags[tag] = ifd[tagKey];
              console.log(`GeoTag ${tag}:`, ifd[tagKey]);
            }
          });
        }
        
        // Extract GPS data from EXIF
        const gpsLatRef = ifd.t1;
        const gpsLat = ifd.t2;
        const gpsLonRef = ifd.t3;
        const gpsLon = ifd.t4;
        
        if (gpsLat && gpsLon) {
          metadata.exifData.gps = {
            latRef: gpsLatRef,
            lat: gpsLat,
            lonRef: gpsLonRef,
            lon: gpsLon
          };
          console.log('GPS data found:', metadata.exifData.gps);
        }
        
        // Create canvas and convert to blob
        const canvas = document.createElement('canvas');
        canvas.width = ifd.width;
        canvas.height = ifd.height;
        const ctx = canvas.getContext('2d');
        
        try {
          const imageData = ctx.createImageData(canvas.width, canvas.height);
          imageData.data.set(rgba);
          ctx.putImageData(imageData, 0, 0);
          
          canvas.toBlob((blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              console.log('TIFF processing completed successfully');
              resolve({ url, metadata, originalBuffer: buffer });
            } else {
              reject(new Error('Failed to create blob from processed TIFF'));
            }
          }, 'image/png', 0.95);
          
        } catch (canvasError) {
          console.error('Canvas processing failed:', canvasError);
          reject(new Error(`Failed to create displayable image: ${canvasError.message}`));
        }
        
      } catch (error) {
        console.error('TIFF processing error:', error);
        
        // Try fallback method for problematic TIFFs
        if (error.message.includes('dimensions') || error.message.includes('No valid image')) {
          console.log('Attempting fallback TIFF processing...');
          try {
            // Try to load as a regular image (some browsers can handle basic TIFFs)
            const fallbackUrl = URL.createObjectURL(file);
            const testImg = new Image();
            
            const fallbackPromise = new Promise((resolve, reject) => {
              testImg.onload = () => {
                console.log('Fallback method successful - browser can handle this TIFF');
                resolve({ 
                  url: fallbackUrl, 
                  metadata: {
                    width: testImg.naturalWidth,
                    height: testImg.naturalHeight,
                    fileInfo: {
                      name: file.name,
                      size: file.size,
                      lastModified: file.lastModified,
                      compression: 'unknown',
                      bitsPerSample: 'unknown'
                    },
                    fallbackMethod: true
                  }, 
                  originalBuffer: e.target.result 
                });
              };
              testImg.onerror = () => {
                URL.revokeObjectURL(fallbackUrl);
                reject(new Error('Both UTIF processing and browser fallback failed'));
              };
            });
            
            testImg.src = fallbackUrl;
            return fallbackPromise;
          } catch (fallbackError) {
            console.error('Fallback processing also failed:', fallbackError);
          }
        }
        
        // Enhanced error message with troubleshooting
        let enhancedMessage = error.message;
        if (error.message.includes('dimensions')) {
          enhancedMessage += '\n\nTroubleshooting steps:\n1. This TIFF may have an unusual structure\n2. Try opening it in an image editor and re-saving as PNG\n3. If it\'s a GeoTIFF, try using GDAL to convert it:\n   gdal_translate input.tif output.png\n4. Check if the file is corrupted by opening it in other software';
        }
        
        reject(new Error(enhancedMessage));
      }
    };
    
    reader.onerror = (error) => {
      console.error('File reader error:', error);
      reject(new Error('Failed to read TIFF file'));
    };
    
    reader.readAsArrayBuffer(file);
  });
};

// Enhanced cropping function with real-time transformations
// Enhanced cropping function with real-time transformations and compression
const getCroppedImg = async (imageSrc, pixelCrop, originalMetadata = null, format = 'jpeg', transformations = {}, maxSizeKB = 10240) => {
  return new Promise(async (resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.src = imageSrc;
    image.onload = async () => {
      const canvas = document.createElement("canvas");
      
      // Optimize crop dimensions for AI processing
      const optimalDims = calculateOptimalDimensions(pixelCrop.width, pixelCrop.height, 2048);
      
      canvas.width = optimalDims.width;
      canvas.height = optimalDims.height;
      const ctx = canvas.getContext("2d");
      
      // Apply transformations
      ctx.save();
      
      const { offsetX = 0, offsetY = 0, rotation = 0, scale = 1 } = transformations;
      
      ctx.translate(canvas.width / 2, canvas.height / 2);
      
      if (rotation !== 0) {
        ctx.rotate((rotation * Math.PI) / 180);
      }
      
      if (scale !== 1) {
        ctx.scale(scale, scale);
      }
      
      // Draw and resize image
      ctx.drawImage(
        image,
        pixelCrop.x + offsetX,
        pixelCrop.y + offsetY,
        pixelCrop.width,
        pixelCrop.height,
        -canvas.width / 2,
        -canvas.height / 2,
        canvas.width,
        canvas.height
      );
      
      ctx.restore();
      
      try {
        // Compress for AI processing
        const compressionResult = await compressImageForAI(canvas, format === 'tiff' ? 'jpeg' : format, maxSizeKB);
        
        console.log(`Cropped and compressed: ${compressionResult.finalSize.toFixed(1)}KB`);
        
        const croppedUrl = URL.createObjectURL(compressionResult.blob);
        resolve({ 
          url: croppedUrl, 
          blob: compressionResult.blob, 
          metadata: {
            ...originalMetadata,
            processedDimensions: optimalDims,
            compressionInfo: compressionResult
          },
          format: format,
          optimized: true
        });
      } catch (compressionError) {
        console.error('Compression failed:', compressionError);
        // Fallback to uncompressed
        const outputFormat = format === 'tiff' ? 'image/png' : `image/${format}`;
        const quality = format === 'jpeg' ? 0.85 : undefined;
        
        canvas.toBlob((blob) => {
          if (blob) {
            const croppedUrl = URL.createObjectURL(blob);
            resolve({ 
              url: croppedUrl, 
              blob, 
              metadata: originalMetadata,
              format: format,
              optimized: false
            });
          } else {
            reject(new Error("Canvas is empty"));
          }
        }, outputFormat, quality);
      }
    };
    image.onerror = (err) => {
      reject(err);
    };
  });
};

// Sliding window calculation helper
const calculateSlidingWindow = (imageWidth, imageHeight, windowSize, position) => {
  const { width: winWidth, height: winHeight } = windowSize;
  const { x: posX, y: posY } = position;
  
  const maxX = imageWidth - winWidth;
  const maxY = imageHeight - winHeight;
  
  const finalX = Math.max(0, Math.min(posX, maxX));
  const finalY = Math.max(0, Math.min(posY, maxY));
  
  return {
    x: finalX,
    y: finalY,
    width: Math.min(winWidth, imageWidth - finalX),
    height: Math.min(winHeight, imageHeight - finalY)
  };
};

// Calculate real-world coordinates from pixel coordinates using GeoTIFF metadata
const pixelToGeoCoordinates = (pixelX, pixelY, geoTags) => {
  if (!geoTags || !geoTags[33550] || !geoTags[33922]) {
    return null;
  }

  const pixelScale = geoTags[33550]; // ModelPixelScaleTag [scaleX, scaleY, scaleZ]
  const tiePoint = geoTags[33922]; // ModelTiepointTag [I, J, K, X, Y, Z]
  
  if (!pixelScale || !tiePoint || pixelScale.length < 2 || tiePoint.length < 6) {
    return null;
  }

  // Calculate real-world coordinates
  const geoX = tiePoint[3] + (pixelX - tiePoint[0]) * pixelScale[0];
  const geoY = tiePoint[4] - (pixelY - tiePoint[1]) * pixelScale[1]; // Note: Y is usually inverted
  
  return {
    longitude: geoX,
    latitude: geoY,
    coordinateSystem: 'projected', // Could be geographic depending on the TIFF
    pixelScale: {
      x: pixelScale[0],
      y: pixelScale[1]
    }
  };
};

// Color palette for multiple object tracking
const OBJECT_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', 
  '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F',
  '#BB8FCE', '#85C1E9', '#F8C471', '#82E0AA',
  '#F1948A', '#85C1E9', '#F9E79F', '#AED6F1'
];

const TimeSeriesPage = () => {
  // Library loading state
  const [librariesLoaded, setLibrariesLoaded] = useState(false);
  const [libraryError, setLibraryError] = useState(null);
  
  // Model selection state
  const [selectedModel, setSelectedModel] = useState("picogram2");
  
  // Number of labels to show (2-16)
  const [numLabels, setNumLabels] = useState(4);
  
  // Arrays to store all image data
  const [topImages, setTopImages] = useState(Array(16).fill(null));
  const [originalImages, setOriginalImages] = useState(Array(16).fill(null)); // Store original uploaded images
  const [predictedImages, setPredictedImages] = useState(Array(16).fill(null));
  const [dimensions, setDimensions] = useState(Array(16).fill(null));
  
  // Multiple object tracking with per-image numbering
  const [selectedObjects, setSelectedObjects] = useState(Array(16).fill(null).map(() => [])); // Array of arrays
  const [imageObjectCounters, setImageObjectCounters] = useState(Array(16).fill(0)); // Per-image counters
  const [showSaveButton, setShowSaveButton] = useState(Array(16).fill(false)); // Show save button after processing
  
  // Store original file information
  const [originalFiles, setOriginalFiles] = useState(Array(16).fill(null));
  const [originalMetadata, setOriginalMetadata] = useState(Array(16).fill(null));
  const [originalDimensions, setOriginalDimensions] = useState(Array(16).fill(null));
          
  const [unifyMode, setUnifyMode] = useState(Array(16).fill(false));
const [unifySelections, setUnifySelections] = useState(Array(16).fill(null).map(() => new Set()));
const [unifiedImages, setUnifiedImages] = useState(Array(16).fill(null));
  // Reference values for calculations
  const [referenceObjects, setReferenceObjects] = useState([]); // Array of reference objects
  const [baselineDimensions, setBaselineDimensions] = useState(null);
  
  // Processing state - per image to allow queue processing
  const [isProcessing, setIsProcessing] = useState(Array(16).fill(false));
  
  const inputRefs = Array(16).fill().map(() => createRef());

  // Enhanced cropping state
  const [croppingIndex, setCroppingIndex] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  
  // Enhanced controls with real-time preview
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);
  const [keepSquare, setKeepSquare] = useState(true);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  
  // Real-time preview state
  const [previewTransformations, setPreviewTransformations] = useState({
    rotation: 0,
    scale: 1,
    offsetX: 0,
    offsetY: 0
  });
  
  // Sliding window controls
  const [windowMode, setWindowMode] = useState('crop');
  const [windowSize, setWindowSize] = useState({ width: 512, height: 512 });
  const [windowPosition, setWindowPosition] = useState({ x: 0, y: 0 });
  const [customWindowSize, setCustomWindowSize] = useState(false);
  
  // Current image info for processing
  const [currentImageInfo, setCurrentImageInfo] = useState(null);
  
  // Temporary state for image preview (before committing)
  const [tempImagePreview, setTempImagePreview] = useState(null);
  const [tempImageInfo, setTempImageInfo] = useState(null);

  // Enhanced library loading on component mount
  useEffect(() => {
    const initializeLibraries = async () => {
      try {
        console.log('Initializing required libraries...');
        await loadRequiredLibraries();
        setLibrariesLoaded(true);
        console.log('All libraries ready for use');
      } catch (error) {
        console.error('Library loading failed:', error);
        setLibraryError(error.message);
      }
    };
    
    initializeLibraries();
  }, []);

  // Update preview transformations in real-time
  useEffect(() => {
    setPreviewTransformations({
      rotation,
      scale,
      offsetX,
      offsetY
    });
  }, [rotation, scale, offsetX, offsetY]);

  // Generate chart data for multiple objects with proper per-image numbering
  const generateChartData = useCallback(() => {
    const datasets = [];
    const labels = Array(numLabels).fill().map((_, idx) => `Time ${idx + 1}`);
    
    // Find the maximum object number across all images to determine how many tracking lines we need
    let maxObjectNumber = 0;
    selectedObjects.slice(0, numLabels).forEach(imageObjects => {
      imageObjects.forEach(obj => {
        maxObjectNumber = Math.max(maxObjectNumber, obj.number);
      });
    });
    
    // Create a dataset for each object number (1, 2, 3, etc.)
    for (let objectNum = 1; objectNum <= maxObjectNumber; objectNum++) {
      const data = Array(numLabels).fill(null);
      
      // Fill data for each time point, looking for objects with the same number
      selectedObjects.slice(0, numLabels).forEach((imageObjects, timeIndex) => {
        const objectAtTime = imageObjects.find(obj => obj.number === objectNum);
        if (objectAtTime) {
          data[timeIndex] = parseFloat(objectAtTime.percentage);
        }
      });
      
      // Only create dataset if there's at least one data point
      if (data.some(value => value !== null)) {
        datasets.push({
          label: `Coral ${objectNum}`,
          data: data,
          fill: false,
          borderColor: OBJECT_COLORS[(objectNum - 1) % OBJECT_COLORS.length],
          backgroundColor: OBJECT_COLORS[(objectNum - 1) % OBJECT_COLORS.length],
          tension: 0.1,
          pointRadius: 5,
          pointHoverRadius: 7,
          connectNulls: false // Don't connect points when data is missing
        });
      }
    }
    
    return { labels, datasets };
  }, [selectedObjects, numLabels]);

  const chartData = generateChartData();

  const chartOptions = {
    responsive: true,
    scales: {
      y: { 
        beginAtZero: true,
        title: {
          display: true,
          text: 'Coral Size (relative %)'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Time Points'
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      title: {
        display: true,
        text: 'Coral Size Tracking Over Time'
      },
      tooltip: {
        callbacks: {
          afterLabel: function(context) {
            // Add pixel count to tooltip
            const timeIndex = context.dataIndex;
            const objectNumber = parseInt(context.dataset.label.split(' ')[1]);
            const imageObjects = selectedObjects[timeIndex] || [];
            const obj = imageObjects.find(o => o.number === objectNumber);
            return obj ? `Pixels: ${obj.pixelCount}` : '';
          }
        }
      }
    }
  };

  // Toggle model selection
  const toggleModel = () => {
    setSelectedModel(prevModel => prevModel === "picogram2" ? "picogram1" : "picogram2");
  };

  const handlePlusClick = (index) => {
    if (!librariesLoaded) {
      window.alert('Please wait for libraries to load before uploading images.');
      return;
    }
    inputRefs[index].current.click();
  };

  // Enhanced file change handler with comprehensive error handling
  const handleFileChange = async (index, event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    console.log(`Processing file: ${file.name}, type: ${file.type}, size: ${file.size} bytes`);
    
    // Check if libraries are loaded
    if (!librariesLoaded) {
      window.alert('Please wait for libraries to load before uploading images.');
      return;
    }
    
    // Check file size (warn if > 100MB)
    // Check file size with enhanced warnings
if (file.size > 200 * 1024 * 1024) {
  const proceed = window.confirm('Very large file detected (>200MB). This may take several minutes and use significant memory. Continue?');
  if (!proceed) return;
} else if (file.size > 100 * 1024 * 1024) {
  const proceed = window.confirm('Large file detected (>100MB). The image will be automatically optimized for processing. Continue?');
  if (!proceed) return;
}
    
    try {
      let previewURL;
      let metadata = null;
      let originalBuffer = null;
      
      // Determine file format
      const fileExtension = file.name.toLowerCase().split('.').pop();
      const isTiff = file.type === 'image/tiff' || 
                    file.type === 'image/tif' || 
                    fileExtension === 'tiff' || 
                    fileExtension === 'tif';
      
      if (isTiff) {
  console.log('Processing TIFF file with metadata preservation...');
  
  try {
    // Use enhanced processing for large files
    if (file.size > 50 * 1024 * 1024) { // 50MB threshold
      console.log('Large TIFF detected, using optimized processing...');
      const tiffData = await processLargeTiffFile(file);
      previewURL = tiffData.url;
      metadata = tiffData.metadata;
      originalBuffer = tiffData.originalBuffer;
      console.log('Large TIFF processed successfully:', metadata);
    } else {
      const tiffData = await processTiffFile(file);
      previewURL = tiffData.url;
      metadata = tiffData.metadata;
      originalBuffer = tiffData.originalBuffer;
      console.log('Standard TIFF processed:', metadata);
    }
  } catch (tiffError) {
    console.error('TIFF processing failed:', tiffError);
    throw tiffError;
  }
} else {
        // Handle regular image formats
        previewURL = URL.createObjectURL(file);
        
        if (window.EXIF && (file.type === 'image/jpeg' || fileExtension === 'jpg' || fileExtension === 'jpeg')) {
          try {
            await new Promise((resolve) => {
              window.EXIF.getData(file, function() {
                const lat = window.EXIF.getTag(this, "GPSLatitude");
                const lon = window.EXIF.getTag(this, "GPSLongitude");
                if (lat && lon) {
                  metadata = {
                    exifData: {
                      gps: {
                        lat: lat,
                        lon: lon,
                        latRef: window.EXIF.getTag(this, "GPSLatitudeRef"),
                        lonRef: window.EXIF.getTag(this, "GPSLongitudeRef")
                      }
                    }
                  };
                  console.log('EXIF GPS data extracted:', metadata);
                }
                resolve();
              });
            });
          } catch (error) {
            console.warn('Could not extract EXIF data:', error);
          }
        }
      }

      // Get image dimensions
      const img = new Image();
      img.src = previewURL;
      img.onload = () => {
        const dims = { 
          width: img.naturalWidth, 
          height: img.naturalHeight 
        };
        
        console.log('Image loaded successfully:', dims);
        
        // Store in temporary state
        setTempImagePreview(previewURL);
        setTempImageInfo({
          index,
          file,
          previewURL,
          dimensions: dims,
          format: fileExtension,
          metadata,
          originalBuffer
        });
        
        setCurrentImageInfo({
          index,
          dimensions: dims,
          format: fileExtension,
          metadata,
          originalBuffer
        });
        
        const defaultWindowSize = Math.min(dims.width, dims.height, 512);
        setWindowSize({ width: defaultWindowSize, height: defaultWindowSize });
        setWindowPosition({ 
          x: Math.max(0, (dims.width - defaultWindowSize) / 2), 
          y: Math.max(0, (dims.height - defaultWindowSize) / 2) 
        });
        
        // Reset states and open cropping modal
        setCroppingIndex(index);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setRotation(0);
        setScale(1);
        setOffsetX(0);
        setOffsetY(0);
        setKeepSquare(true);
        setWindowMode('crop');
      };
      
      img.onerror = (error) => {
        console.error('Image load error:', error);
        throw new Error('Failed to load processed image');
      };
      
    } catch (error) {
      console.error('Error processing file:', error);
      
      // Provide user-friendly error messages
      let userMessage = `Error processing ${file.name}:\n\n${error.message}`;
      
      if (error.message.includes('compression')) {
        userMessage += '\n\n💡 Solution: Try converting your TIFF to PNG or use uncompressed TIFF format.';
      } else if (error.message.includes('UTIF library')) {
        userMessage += '\n\n💡 Solution: Please refresh the page and wait for libraries to load completely.';
      } else if (error.message.includes('dimensions') || error.message.includes('No valid image')) {
        userMessage = `Your TIFF file "${file.name}" has an unusual structure that our processor can't handle.\n\n`;
        userMessage += `🔧 Quick fixes to try:\n`;
        userMessage += `1. Open the file in GIMP, Photoshop, or other image editor\n`;
        userMessage += `2. Export/Save As → PNG format\n`;
        userMessage += `3. Upload the PNG version instead\n\n`;
        userMessage += `🗺️ For GeoTIFF files:\n`;
        userMessage += `Use GDAL command: gdal_translate input.tif output.png\n`;
        userMessage += `Or try QGIS: Layer → Export → Save As → PNG\n\n`;
        userMessage += `📁 File details: ${(file.size / (1024*1024)).toFixed(1)}MB`;
      }
      
      window.alert(userMessage);
    }
  };
// Function to unify selected objects in an image
const unifySelectedObjects = async (imageIndex) => {
  const selections = unifySelections[imageIndex];
  if (selections.size < 2) {
    alert('Please select at least 2 objects to unify');
    return;
  }
  
  
  const imgUrl = predictedImages[imageIndex];
  if (!imgUrl) return;
  
  // Create a unified color (average of selected colors or pick the first one)
  const selectedColors = Array.from(selections).map(colorStr => {
    const [r, g, b] = colorStr.split(',').map(Number);
    return { r, g, b };
  });
  
  // Use the first selected color as the unified color
  const unifiedColor = selectedColors[0];
  
  // Process the image
  const img = new Image();
  img.crossOrigin = "Anonymous";
  img.src = imgUrl;
  
  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    
    // Replace all selected colors with the unified color
    const tolerance = 20;
    for (let i = 0; i < pixels.length; i += 4) {
      const r = pixels[i];
      const g = pixels[i + 1];
      const b = pixels[i + 2];
      
      // Check if this pixel matches any selected color
      for (const color of selectedColors) {
        if (
          Math.abs(r - color.r) < tolerance &&
          Math.abs(g - color.g) < tolerance &&
          Math.abs(b - color.b) < tolerance
        ) {
          // Replace with unified color
          pixels[i] = unifiedColor.r;
          pixels[i + 1] = unifiedColor.g;
          pixels[i + 2] = unifiedColor.b;
          break;
        }
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
    
    // Convert to data URL and store
    const unifiedUrl = canvas.toDataURL('image/png');
    setUnifiedImages(prev => {
      const newUnified = [...prev];
      newUnified[imageIndex] = unifiedUrl;
      return newUnified;
    });
    
    // Exit unify mode for this image
    setUnifyMode(prev => {
      const newMode = [...prev];
      newMode[imageIndex] = false;
      return newMode;
    });
    
    // Clear selections
    setUnifySelections(prev => {
      const newSelections = [...prev];
      newSelections[imageIndex] = new Set();
      return newSelections;
    });
    
    // Clear previous coral selections for this image (they'll need to reselect on the unified image)
    setSelectedObjects(prev => {
      const newSelected = [...prev];
      newSelected[imageIndex] = [];
      return newSelected;
    });
    
    // Reset object counter for this image
    setImageObjectCounters(prev => {
      const newCounters = [...prev];
      newCounters[imageIndex] = 0;
      return newCounters;
    });
    
    console.log(`Unified ${selections.size} objects in image ${imageIndex}`);
  };
};
// Handle clicks when in unify mode
const handleUnifyModeClick = (index, event) => {
  if (!unifyMode[index]) {
    handlePredictedClick(index, event);
    return;
  }
  
  const imgUrl = unifiedImages[index] || predictedImages[index];
  if (!imgUrl) return;
  
  const img = new Image();
  img.crossOrigin = "Anonymous";
  img.src = imgUrl;
  
  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    
    const rect = event.target.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;
    
    const scaleX = img.naturalWidth / rect.width;
    const scaleY = img.naturalHeight / rect.height;
    const imgX = Math.floor(clickX * scaleX);
    const imgY = Math.floor(clickY * scaleY);
    
    const clickedColor = ctx.getImageData(imgX, imgY, 1, 1).data;
    const colorKey = `${clickedColor[0]},${clickedColor[1]},${clickedColor[2]}`;
    
    // Toggle selection
    setUnifySelections(prev => {
      const newSelections = [...prev];
      const currentSet = new Set(newSelections[index]);
      
      if (currentSet.has(colorKey)) {
        currentSet.delete(colorKey);
      } else {
        currentSet.add(colorKey);
      }
      
      newSelections[index] = currentSet;
      return newSelections;
    });
  };
};
  const handleCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  // Save processed image with format preservation
  const handleSaveImage = async (index) => {
    const originalFile = originalFiles[index];
    const format = originalFile?.name.toLowerCase().split('.').pop() || 'jpg';
    const metadata = originalMetadata[index];
    
    try {
      // Create download link for the processed image
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `processed_${timestamp}.${format}`;
      
      link.href = topImages[index];
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log(`Image saved as: ${filename}`);
      console.log('Preserved metadata:', metadata);
    } catch (error) {
      console.error('Error saving image:', error);
      window.alert('Error saving image. Please try again.');
    }
  };

  // Download JSON for specific image with TIFF coordinate preservation
  const handleDownloadImageJSON = (imageIndex) => {
    try {
      const imageObjects = selectedObjects[imageIndex] || [];
      const originalFile = originalFiles[imageIndex];
      const metadata = originalMetadata[imageIndex];
      const dims = dimensions[imageIndex];
      const origDims = originalDimensions[imageIndex];

      if (imageObjects.length === 0) {
        window.alert('No coral selections found for this image.');
        return;
      }

      const isFromTiff = dims?.cropMapping?.originalFormat === 'tiff' || dims?.cropMapping?.originalFormat === 'tif';

      const imageData = {
        metadata: {
          timestamp: new Date().toISOString(),
          model_used: selectedModel,
          analysis_type: "single_image_coral_analysis",
          // ✅ Include TIFF processing info
          processing_info: {
            original_format: dims?.cropMapping?.originalFormat || originalFile?.name.toLowerCase().split('.').pop() || 'unknown',
            was_converted_for_ai: isFromTiff,
            coordinate_system: isFromTiff ? 'original_tiff_coordinates' : 'image_coordinates'
          }
        },
        image: {
          image_index: imageIndex,
          time_point: imageIndex + 1,
          filename: originalFile?.name || `image_${imageIndex}`,
          format: originalFile?.name.toLowerCase().split('.').pop() || 'unknown',
          // ✅ Use original TIFF dimensions, not cropped dimensions
          dimensions: origDims ? {
            width: origDims.width,
            height: origDims.height
          } : (dims ? { width: dims.width, height: dims.height } : null),
          // ✅ Include cropping information for TIFF files
          crop_info: dims?.cropMapping ? {
            cropped_from_original: true,
            crop_offset: dims.cropMapping.cropOffset,
            crop_dimensions: dims.cropMapping.croppedDimensions,
            original_dimensions: dims.cropMapping.originalDimensions
          } : null,
          corals: []
        }
      };

      // Add geolocation if available (using original TIFF metadata)
      if (metadata?.geoTags && Object.keys(metadata.geoTags).length > 0) {
        imageData.image.geolocation = {
          coordinate_system: "projected",
          pixel_scale: metadata.geoTags[33550] ? {
            x: metadata.geoTags[33550][0],
            y: metadata.geoTags[33550][1]
          } : null,
          tie_point: metadata.geoTags[33922] ? metadata.geoTags[33922] : null,
          has_geotags: true,
          source: "original_tiff_metadata"
        };
      } else if (metadata?.exifData?.gps) {
        imageData.image.geolocation = {
          coordinate_system: "geographic",
          gps_data: metadata.exifData.gps,
          has_geotags: true,
          source: "exif_data"
        };
      }

      // Process each coral in this image
      imageObjects.forEach(coral => {
        const coralData = {
          coral_number: coral.number,
          pixel_count: coral.pixelCount,
          percentage: parseFloat(coral.percentage),
          // ✅ Use original TIFF coordinates for JSON export
          click_position: {
            x: coral.originalClickX !== undefined ? coral.originalClickX : coral.clickX,
            y: coral.originalClickY !== undefined ? coral.originalClickY : coral.clickY,
            coordinate_system: coral.originalClickX !== undefined ? 'original_tiff' : 'processed_image'
          },
          // ✅ Include both coordinate systems if available
          coordinates: {
            original_tiff: coral.originalClickX !== undefined ? {
              x: coral.originalClickX,
              y: coral.originalClickY
            } : null,
            processed_image: {
              x: coral.clickX,
              y: coral.clickY
            }
          },
          color: coral.color
        };

        // ✅ Add real-world coordinates using ORIGINAL TIFF coordinates
        if (metadata?.geoTags && coral.originalClickX !== undefined && coral.originalClickY !== undefined) {
          const geoCoords = pixelToGeoCoordinates(coral.originalClickX, coral.originalClickY, metadata.geoTags);
          if (geoCoords) {
            coralData.world_coordinates = {
              ...geoCoords,
              source: "original_tiff_geotags",
              calculated_from: "original_tiff_coordinates"
            };
          }
        } else if (metadata?.geoTags && coral.clickX !== undefined && coral.clickY !== undefined) {
          // Fallback: if no original coordinates, try with processed coordinates (less accurate)
          const geoCoords = pixelToGeoCoordinates(coral.clickX, coral.clickY, metadata.geoTags);
          if (geoCoords) {
            coralData.world_coordinates = {
              ...geoCoords,
              source: "original_tiff_geotags",
              calculated_from: "processed_image_coordinates",
              accuracy_note: "Coordinates may be less accurate due to cropping/processing"
            };
          }
        }

        imageData.image.corals.push(coralData);
      });

      // Create and download JSON file
      const jsonString = JSON.stringify(imageData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = originalFile?.name.replace(/\.[^/.]+$/, '') || `image_${imageIndex}`;
      link.href = url;
      link.download = `${filename}_coral_analysis_${timestamp}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      
      const coordSystem = isFromTiff ? "original TIFF coordinates" : "image coordinates";
      console.log(`✅ JSON analysis data downloaded for image ${imageIndex} with ${coordSystem}`);
      if (isFromTiff) {
        console.log('🗺️ TIFF metadata and geolocation preserved in JSON export');
      }
      
    } catch (error) {
      console.error('Error creating JSON download:', error);
      window.alert('Error creating JSON file. Please try again.');
    }
  };

  // Download mask for selected corals
// Download mask for selected corals
const handleDownloadMask = async (imageIndex) => {
  try {
    const imageObjects = selectedObjects[imageIndex] || [];
    // Use unified image if available, otherwise use predicted image
    const imageUrl = unifiedImages[imageIndex] || predictedImages[imageIndex];
    
    if (imageObjects.length === 0) {
      window.alert('No coral selections found for this image.');
      return;
    }
    
    if (!imageUrl) {
      window.alert('No image available for mask generation.');
      return;
    }
    
    // Load the image
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.src = imageUrl;
    
    img.onload = () => {
      // Create canvas for mask generation
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      
      // Draw the image to get pixel data
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, img.naturalWidth, img.naturalHeight);
      const pixels = imageData.data;
      
      // Create mask data (initially all black)
      const maskData = new Uint8ClampedArray(pixels.length);
      
      // Set all pixels to black initially (RGBA: 0,0,0,255)
      for (let i = 0; i < maskData.length; i += 4) {
        maskData[i] = 0;     // Red
        maskData[i + 1] = 0; // Green
        maskData[i + 2] = 0; // Blue
        maskData[i + 3] = 255; // Alpha
      }
      
      // If the image is unified, we need to extract all unique colors from the unified image
      // that correspond to coral objects
      const tolerance = 20;
      
      if (unifiedImages[imageIndex]) {
        // For unified images, find all pixels matching the selected coral colors
        // The unified image already has all segments of the same type merged
        imageObjects.forEach(coral => {
          const targetColor = coral.color;
          
          for (let i = 0; i < pixels.length; i += 4) {
            const r = pixels[i];
            const g = pixels[i + 1];
            const b = pixels[i + 2];
            
            // Check if pixel matches this coral's color within tolerance
            if (
              Math.abs(r - targetColor.r) < tolerance &&
              Math.abs(g - targetColor.g) < tolerance &&
              Math.abs(b - targetColor.b) < tolerance
            ) {
              // Set to white (255, 255, 255, 255)
              maskData[i] = 255;     // Red
              maskData[i + 1] = 255; // Green
              maskData[i + 2] = 255; // Blue
              maskData[i + 3] = 255; // Alpha
            }
          }
        });
      } else {
        // For non-unified images, use the original logic
        imageObjects.forEach(coral => {
          const targetColor = coral.color;
          
          for (let i = 0; i < pixels.length; i += 4) {
            const r = pixels[i];
            const g = pixels[i + 1];
            const b = pixels[i + 2];
            
            if (
              Math.abs(r - targetColor.r) < tolerance &&
              Math.abs(g - targetColor.g) < tolerance &&
              Math.abs(b - targetColor.b) < tolerance
            ) {
              maskData[i] = 255;
              maskData[i + 1] = 255;
              maskData[i + 2] = 255;
              maskData[i + 3] = 255;
            }
          }
        });
      }
      
      // Create new image data with mask
      const maskImageData = new ImageData(maskData, canvas.width, canvas.height);
      
      // Clear canvas and draw mask
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.putImageData(maskImageData, 0, 0);
      
      // Convert to blob and download
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          const originalFile = originalFiles[imageIndex];
          const baseName = originalFile?.name.replace(/\\.[^/.]+$/, '') || `image_${imageIndex}`;
          const unifiedSuffix = unifiedImages[imageIndex] ? '_unified' : '';
          
          link.href = url;
          link.download = `${baseName}_coral_mask${unifiedSuffix}_${timestamp}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          URL.revokeObjectURL(url);
          console.log(`Coral mask downloaded for image ${imageIndex}${unifiedSuffix}`);
        }
      }, 'image/png');
    };
    
    img.onerror = (err) => {
      console.error("Error loading image for mask generation:", err);
      window.alert("Error loading image for mask generation.");
    };
    
  } catch (error) {
    console.error('Error creating coral mask:', error);
    window.alert('Error creating coral mask. Please try again.');
  }
};
  // Process & Analyze with TIFF-to-PNG conversion for AI model
  const handleCropSave = async () => {
    try {
      const currentIndex = croppingIndex;
      
      // Commit the temporary image data to main state
      if (tempImageInfo) {
        // Store original file info
        setOriginalFiles((prev) => {
          const newFiles = [...prev];
          newFiles[currentIndex] = tempImageInfo.file;
          return newFiles;
        });
        
        // Store metadata and original image
        setOriginalMetadata((prev) => {
          const newMetadata = [...prev];
          newMetadata[currentIndex] = tempImageInfo.metadata;
          return newMetadata;
        });
        
        setOriginalImages((prev) => {
          const newImages = [...prev];
          newImages[currentIndex] = tempImageInfo.previewURL;
          return newImages;
        });
        
        setDimensions((prev) => {
          const newDims = [...prev];
          newDims[currentIndex] = tempImageInfo.dimensions;
          return newDims;
        });
        
        setOriginalDimensions((prev) => {
          const newDims = [...prev];
          newDims[currentIndex] = tempImageInfo.dimensions;
          return newDims;
        });
      }
      
      // Set processing state for this specific image
      setIsProcessing(prev => {
        const newProcessing = [...prev];
        newProcessing[currentIndex] = true;
        return newProcessing;
      });
      
      let finalCropArea;
      let sourceImage = tempImagePreview || topImages[croppingIndex];
      
      if (windowMode === 'sliding') {
        const imgDims = currentImageInfo.dimensions;
        finalCropArea = calculateSlidingWindow(imgDims.width, imgDims.height, windowSize, windowPosition);
      } else {
        finalCropArea = croppedAreaPixels;
      }
      
      // ✅ SOLUTION: Always convert to PNG for AI model, regardless of original format
      const originalFormat = currentImageInfo.format;
      const isOriginallyTiff = originalFormat === 'tiff' || originalFormat === 'tif';
      
      console.log(`Original format: ${originalFormat}, converting to PNG for AI model`);
      
      const croppedResult = await getCroppedImg(
        sourceImage, 
        finalCropArea, 
        currentImageInfo.metadata, 
        'png', // ✅ Always use PNG for AI model
        previewTransformations
      );
      
      // Store the cropped image in main state (keeping original format info)
      setTopImages((prev) => {
        const newImages = [...prev];
        newImages[croppingIndex] = croppedResult.url;
        return newImages;
      });
      
      // Store crop dimensions with coordinate mapping info
      setDimensions((prev) => {
        const newDims = [...prev];
        newDims[croppingIndex] = {
          width: finalCropArea.width,
          height: finalCropArea.height,
          // ✅ Store coordinate mapping info for TIFF files
          cropMapping: isOriginallyTiff ? {
            originalFormat: originalFormat,
            cropOffset: {
              x: finalCropArea.x,
              y: finalCropArea.y
            },
            originalDimensions: currentImageInfo.dimensions,
            croppedDimensions: {
              width: finalCropArea.width,
              height: finalCropArea.height
            }
          } : null
        };
        return newDims;
      });
      
      // Clear temporary state and close modal
      setTempImagePreview(null);
      setTempImageInfo(null);
      setCroppingIndex(null);

      // ✅ Send PNG to AI model with proper filename
      const formData = new FormData();
      const aiFilename = isOriginallyTiff ? 
        `cropped_from_${tempImageInfo?.file?.name || 'tiff'}.png` : 
        `cropped.${originalFormat}`;
        
      formData.append("image", croppedResult.blob, aiFilename);
      formData.append("modelVersion", selectedModel);
      
      console.log(`Sending ${aiFilename} to AI model (converted from ${originalFormat})`);
      
      try {
        const apiBaseUrl = getApiBaseUrl();
        const apiUrl = `${apiBaseUrl}/api/upload`;

        const apiRes = await fetch(apiUrl, {
          method: "POST",
          body: formData,
        });
        
        if (!apiRes.ok) throw new Error("API request failed");
        
        const data = await apiRes.json();
        console.log("AI model response:", data);
        
        setPredictedImages((prev) => {
          const newPred = [...prev];
          newPred[currentIndex] = data.predicted;
          return newPred;
        });
        
        // Show save button after successful processing
        setShowSaveButton((prev) => {
          const newShow = [...prev];
          newShow[currentIndex] = true;
          return newShow;
        });
        
        if (isOriginallyTiff) {
          console.log('✅ TIFF processing complete: Original TIFF metadata preserved, AI processed PNG prediction');
        }
        
      } catch (error) {
        console.error("Error processing image with AI model:", error);
        if (isOriginallyTiff) {
          window.alert("Error processing TIFF with AI model. The image was converted to PNG for processing, but the AI service failed. Please check if the AI service is running on localhost:4000");
        } else {
          window.alert("Error processing image with AI model.");
        }
      } finally {
        // Clear processing state for this image
        setIsProcessing(prev => {
          const newProcessing = [...prev];
          newProcessing[currentIndex] = false;
          return newProcessing;
        });
      }
    } catch (e) {
      console.error(e);
      window.alert("Error processing image.");
      // Clear processing state on error
      if (croppingIndex !== null) {
        setIsProcessing(prev => {
          const newProcessing = [...prev];
          newProcessing[croppingIndex] = false;
          return newProcessing;
        });
      }
    }
  };

  // Cancel handler that clears temporary state
  const handleCropCancel = () => {
    // Clear temporary state without committing anything
    setTempImagePreview(null);
    setTempImageInfo(null);
    setCroppingIndex(null);
    setCurrentImageInfo(null);
    
    console.log('Upload cancelled - image not loaded');
  };

  const handleEdit = (index) => {
    // Show original image and setup for editing
    setCurrentImageInfo({
      index,
      dimensions: originalDimensions[index],
      format: originalFiles[index]?.name.toLowerCase().split('.').pop() || 'jpg',
      metadata: originalMetadata[index]
    });
    
    // Reset to original image for editing
    if (originalImages[index]) {
      setTopImages((prev) => {
        const newImages = [...prev];
        newImages[index] = originalImages[index];
        return newImages;
      });
    }
    
    setCroppingIndex(index);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setScale(1);
    setOffsetX(0);
    setOffsetY(0);
    setKeepSquare(true);
    setWindowMode('crop');
  };

  // Enhanced multiple object selection with TIFF coordinate mapping
  const handlePredictedClick = (index, event) => {
  const imgUrl = unifiedImages[index] || predictedImages[index];
      if (!imgUrl) return;
    
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.src = imgUrl;
    img.onload = () => {
      const origDims = originalDimensions[index];
      const cropDims = dimensions[index];
      
      if (!origDims) {
        console.error("Missing original dimensions for image", index);
        return;
      }
      
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      
      const rect = event.target.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const clickY = event.clientY - rect.top;
      
      const scaleX = img.naturalWidth / rect.width;
      const scaleY = img.naturalHeight / rect.height;
      const imgX = Math.floor(clickX * scaleX);
      const imgY = Math.floor(clickY * scaleY);

      // ✅ SOLUTION: Map coordinates back to original TIFF coordinate system
      let originalCoordX = imgX;
      let originalCoordY = imgY;
      
      // If this was cropped from a TIFF, map back to original coordinates
      if (cropDims?.cropMapping) {
        const mapping = cropDims.cropMapping;
        originalCoordX = imgX + mapping.cropOffset.x;
        originalCoordY = imgY + mapping.cropOffset.y;
        
        console.log(`🗺️ Coordinate mapping: Predicted (${imgX}, ${imgY}) → Original TIFF (${originalCoordX}, ${originalCoordY})`);
        console.log('📍 Crop offset:', mapping.cropOffset);
      }

      const clickedColor = ctx.getImageData(imgX, imgY, 1, 1).data;
      const targetColor = {
        r: clickedColor[0],
        g: clickedColor[1],
        b: clickedColor[2]
      };
      
      // Check if this color is already selected in this specific image
      const currentObjects = selectedObjects[index] || [];
      const existingObjectIndex = currentObjects.findIndex(obj => 
        Math.abs(obj.color.r - targetColor.r) < 20 &&
        Math.abs(obj.color.g - targetColor.g) < 20 &&
        Math.abs(obj.color.b - targetColor.b) < 20
      );
      
      if (existingObjectIndex !== -1) {
        // Object already selected, remove it (deselect)
        const newSelectedObjects = currentObjects.filter((_, i) => i !== existingObjectIndex);
        
        setSelectedObjects(prev => {
          const newSelected = [...prev];
          newSelected[index] = newSelectedObjects;
          return newSelected;
        });
        
        // If all objects are deselected in this image, reset the counter for this image
        if (newSelectedObjects.length === 0) {
          setImageObjectCounters(prev => {
            const newCounters = [...prev];
            newCounters[index] = 0;
            return newCounters;
          });
          console.log(`All objects deselected in image ${index}. Counter reset to 0.`);
        }
        
        return;
      }
      
      // Count pixels for the new selection
      const tolerance = 20;
      const imageData = ctx.getImageData(0, 0, img.naturalWidth, img.naturalHeight).data;
      let pixelCount = 0;
      
      for (let i = 0; i < imageData.length; i += 4) {
        const r = imageData[i];
        const g = imageData[i + 1];
        const b = imageData[i + 2];
        
        if (
          Math.abs(r - targetColor.r) < tolerance &&
          Math.abs(g - targetColor.g) < tolerance &&
          Math.abs(b - targetColor.b) < tolerance
        ) {
          pixelCount++;
        }
      }
      
      // Use per-image counter and increment it
      const currentImageCounter = imageObjectCounters[index];
      const newObjectNumber = currentImageCounter + 1;
      
      // Update the counter for this specific image
      setImageObjectCounters(prev => {
        const newCounters = [...prev];
        newCounters[index] = newObjectNumber;
        return newCounters;
      });
      
      if (index === 0) {
        // First image - set as reference
        const newObject = {
          number: newObjectNumber,
          color: targetColor,
          pixelCount: pixelCount,
          percentage: "100.0",
          absolute: pixelCount,
          clickX: imgX, // Predicted image coordinates
          clickY: imgY,
          // ✅ Store original TIFF coordinates for JSON export
          originalClickX: originalCoordX,
          originalClickY: originalCoordY,
          coordinateMapping: cropDims?.cropMapping || null
        };
        
        setSelectedObjects(prev => {
          const newSelected = [...prev];
          newSelected[0] = [...(newSelected[0] || []), newObject];
          return newSelected;
        });
        
        setReferenceObjects(prev => [...prev, newObject]);
        setBaselineDimensions({
          width: origDims.width,
          height: origDims.height,
          area: origDims.width * origDims.height
        });
        
        console.log(`🎯 Baseline object ${newObjectNumber} in image ${index}: ${pixelCount} pixels`);
        console.log(`📍 Predicted coords: (${imgX}, ${imgY}), Original coords: (${originalCoordX}, ${originalCoordY})`);
      } else {
        // Calculate percentage relative to corresponding object number in first image
        let percentage = 0;
        let referencePixelCount = null;
        
        // Find object with same number in the first image
        const firstImageObjects = selectedObjects[0] || [];
        const correspondingReferenceObject = firstImageObjects.find(obj => obj.number === newObjectNumber);
        
        if (correspondingReferenceObject) {
          referencePixelCount = correspondingReferenceObject.pixelCount;
          percentage = (pixelCount / referencePixelCount) * 100;
        } else {
          // If no corresponding object in first image, use the first available reference
          if (firstImageObjects.length > 0) {
            referencePixelCount = firstImageObjects[0].pixelCount;
            percentage = (pixelCount / referencePixelCount) * 100;
          }
        }
        
        const newObject = {
          number: newObjectNumber,
          color: targetColor,
          pixelCount: pixelCount,
          percentage: percentage.toFixed(1),
          absolute: pixelCount,
          clickX: imgX, // Predicted image coordinates
          clickY: imgY,
          // ✅ Store original TIFF coordinates for JSON export
          originalClickX: originalCoordX,
          originalClickY: originalCoordY,
          coordinateMapping: cropDims?.cropMapping || null
        };
        
        setSelectedObjects(prev => {
          const newSelected = [...prev];
          newSelected[index] = [...(newSelected[index] || []), newObject];
          return newSelected;
        });
        
        console.log(`🎯 Object ${newObjectNumber} in image ${index}: ${pixelCount} pixels (${percentage.toFixed(1)}%)`);
        console.log(`📍 Predicted coords: (${imgX}, ${imgY}), Original coords: (${originalCoordX}, ${originalCoordY})`);
      }
    };
    
    img.onerror = (err) => {
      console.error("Error loading image for segmentation analysis:", err);
    };
  };

  const numRows = Math.ceil(numLabels / 4);

  // Show loading screen if libraries aren't ready
  if (!librariesLoaded) {
    return (
      <div className="timeseries-container">
        <div className="title-container">
          <h1>Time Series Analysis</h1>
        </div>
        <div className="loading-screen" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          background: 'rgba(0,0,0,0.8)',
          borderRadius: '10px',
          color: '#fff',
          margin: '20px'
        }}>
          {libraryError ? (
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ color: '#ff6b6b' }}>⚠️ Library Loading Failed</h3>
              <p>{libraryError}</p>
              <button 
                onClick={() => window.location.reload()} 
                style={{
                  padding: '10px 20px',
                  background: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                Reload Page
              </button>
            </div>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                border: '3px solid #f3f3f3',
                borderTop: '3px solid #4CAF50',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }}></div>
              <h3>Loading TIFF Processing Libraries...</h3>
              <p>Please wait while we load the required libraries for image processing.</p>
              <small style={{ opacity: 0.7 }}>This may take a few seconds on first load.</small>
            </div>
          )}
        </div>
        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="timeseries-container">
      <div className="title-container">
        <h1>Time Series Analysis</h1>
      </div>
      <p className="upload-instruction">
        Upload your time series images (JPEG, PNG, TIFF with GeoTIFF support)
      </p>
      
      {/* TIFF Help Notice */}
      <div style={{
        background: 'rgba(76, 175, 80, 0.1)',
        border: '1px solid rgba(76, 175, 80, 0.3)',
        borderRadius: '8px',
        padding: '12px',
        margin: '10px 0',
        fontSize: '0.9em',
        color: '#2E7D32'
      }}>
        <strong>🗺️TIFF Processing:</strong> TIFF files are automatically converted to PNG for AI processing while preserving original metadata and coordinates for accurate JSON exports.
      </div>
      
      {/* Model Selector */}
      <div className="model-selector-container">
        <button 
          className={`model-button ${selectedModel === 'picogram2' ? 'active' : ''}`}
          onClick={toggleModel}
        >
          {selectedModel === 'picogram2' ? 'Using: Picogram 2.0' : 'Using: Picogram 1.0'}
        </button>
        <p className="model-description">
          {selectedModel === 'picogram2' 
            ? 'Latest Model with Enhanced Segmentation' 
            : 'Legacy Model for Compatibility'}
        </p>
      </div>
      
      {/* Slider for selecting number of labels */}
      <div className="slider-container">
        <label htmlFor="num-labels">Number of images: {numLabels}</label>
        <input
          type="range"
          id="num-labels"
          min="2"
          max="16"
          value={numLabels}
          onChange={(e) => setNumLabels(parseInt(e.target.value))}
        />
      </div>
      
      <div className="grid-frame">
        {/* TOP ROW(s): Uploaded images */}
        {Array.from({ length: numRows }).map((_, rowIndex) => (
          <div key={`top-row-${rowIndex}`} className="row">
            {Array.from({ length: Math.min(4, numLabels - rowIndex * 4) }).map((_, colIndex) => {
              const index = rowIndex * 4 + colIndex;
              return (
                <div key={`top-${index}`} className="square">
                  {topImages[index] ? (
                    <>
                      <img src={topImages[index]} alt={`Uploaded ${index}`} />
                      {dimensions[index] && (
                        <div className="dimension-label">
                          {dimensions[index].width}px × 
                          {dimensions[index].height}px
                          {originalMetadata[index]?.exifData?.gps && (
                            <div style={{fontSize: '0.7em', color: '#4CAF50'}}>📍 GPS</div>
                          )}
                          {originalMetadata[index]?.geoTags && Object.keys(originalMetadata[index].geoTags).length > 0 && (
                            <div style={{fontSize: '0.7em', color: '#4CAF50'}}>🗺️ GeoTIFF</div>
                          )}
                          {dimensions[index]?.cropMapping && (
                            <div style={{fontSize: '0.7em', color: '#FF9800'}}>🔄 TIFF→PNG</div>
                          )}
                        </div>
                      )}
                      <div className="image-controls">
                        {showSaveButton[index] && (
                          <button 
                            className="save-btn" 
                            onClick={() => handleSaveImage(index)}
                            title="Save processed image"
                          >
                            💾 Save
                          </button>
                        )}
                        {selectedObjects[index] && selectedObjects[index].length > 0 && (
                          <>
                            <button 
                              className="json-btn" 
                              onClick={() => handleDownloadImageJSON(index)}
                              title="Download JSON analysis for this image"
                            >
                              📊 JSON
                            </button>
                            <button 
                              className="mask-btn" 
                              onClick={() => handleDownloadMask(index)}
                              title="Download coral mask (white corals, black background)"
                            >
                              🎭 Mask
                            </button>
                          </>
                        )}
                        <button 
                          className="edit-btn" 
                          onClick={() => handleEdit(index)}
                          title="Edit image"
                        >
                          ✏️ Edit
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="plus" onClick={() => handlePlusClick(index)}>
                      +
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*,.tiff,.tif"
                    ref={inputRefs[index]}
                    style={{ display: "none" }}
                    onChange={(e) => handleFileChange(index, e)}
                  />
                </div>
              );
            })}
          </div>
        ))}

        {/* BOTTOM ROW(s): Predicted segmentation images */}
{Array.from({ length: numRows }).map((_, rowIndex) => (
  <div key={`bottom-row-${rowIndex}`} className="row">
    {Array.from({ length: Math.min(4, numLabels - rowIndex * 4) }).map((_, colIndex) => {
      const index = rowIndex * 4 + colIndex;
      const imageObjects = selectedObjects[index] || [];
      const currentUnifySelections = unifySelections[index];
      const isInUnifyMode = unifyMode[index];
      const displayImage = unifiedImages[index] || predictedImages[index];
     
      return (
        <div key={`bottom-${index}`} className="square">
          {displayImage ? (
            <>
              <img
                src={displayImage}
                alt={`Predicted ${index}`}
                onClick={(e) => handleUnifyModeClick(index, e)}
                style={{
                  cursor: isInUnifyMode ? "crosshair" : "pointer",
                  filter: isInUnifyMode ? "brightness(0.8)" : "none",
                  border: isInUnifyMode ? "3px solid #673ab7" : "none"
                }}
              />
             
              {/* Selection highlights for unify mode */}
              {isInUnifyMode && currentUnifySelections.size > 0 && (
                <div className="unify-selection-overlay">
                  {Array.from(currentUnifySelections).map((colorKey, idx) => {
                    const [r, g, b] = colorKey.split(',').map(Number);
                    return (
                      <div
                        key={idx}
                        className="unify-selection-badge"
                        style={{
                          backgroundColor: `rgb(${r}, ${g}, ${b})`,
                          border: '2px solid white'
                        }}
                      />
                    );
                  })}
                </div>
              )}
             
              {imageObjects.length > 0 && !isInUnifyMode && (
                <div className="selected-objects-info">
                  {imageObjects.map((obj, objIndex) => (
                    <div key={objIndex} className="object-info" style={{
                      background: `rgba(${obj.color.r}, ${obj.color.g}, ${obj.color.b}, 0.8)`,
                      color: '#fff',
                      fontSize: '0.8rem',
                      padding: '2px 4px',
                      margin: '1px',
                      borderRadius: '3px'
                    }}>
                      #{obj.number}: {obj.percentage}%
                      <div style={{ fontSize: '0.7rem', opacity: 0.9 }}>
                        {obj.pixelCount}px
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : isProcessing[index] ? (
            <div className="processing">
              {dimensions[index]?.cropMapping ? 'Converting TIFF→PNG for AI...' : 'Processing...'}
            </div>
          ) : (
            <div className="placeholder">Waiting...</div>
          )}
        </div>
      );
    })}
  </div>
))}
      </div>
      {/* Global Unify Controls - Only show if there are predicted images */}
{predictedImages.some(img => img !== null) && (
  <div className="unify-control-section">
    <h3>🔗 Unify Segmented Objects</h3>
    <p>Select an image to merge multiple segments into one</p>
    <div className="unify-image-selector">
      {Array.from({ length: numLabels }).map((_, index) => {
        const hasPrediction = predictedImages[index] !== null;
        const isActive = unifyMode[index];
        
        return hasPrediction && (
          <button
            key={index}
            className={`unify-image-btn ${isActive ? 'active' : ''}`}
            onClick={() => {
              // Reset all other unify modes
              setUnifyMode(prev => {
                const newMode = Array(16).fill(false);
                newMode[index] = !prev[index];
                return newMode;
              });
              
              // Clear selections if exiting unify mode
              if (unifyMode[index]) {
                setUnifySelections(prev => {
                  const newSelections = [...prev];
                  newSelections[index] = new Set();
                  return newSelections;
                });
              }
            }}
          >
            Image {index + 1}
          </button>
        );
      })}
    </div>
    
    {/* Show active unify controls */}
    {unifyMode.some(mode => mode) && (
      <div className="active-unify-controls">
        <div className="unify-status">
          <span className="image-label">Image {unifyMode.findIndex(mode => mode) + 1}:</span>
          <span className="selection-count">{unifySelections[unifyMode.findIndex(mode => mode)].size} objects selected</span>
        </div>
        <div className="unify-actions">
          <button
            className="unify-done-btn"
            onClick={() => unifySelectedObjects(unifyMode.findIndex(mode => mode))}
            disabled={unifySelections[unifyMode.findIndex(mode => mode)].size < 2}
          >
            ✓ Merge Selected
          </button>
          <button
            className="unify-cancel-btn"
            onClick={() => {
              const activeIndex = unifyMode.findIndex(mode => mode);
              setUnifyMode(prev => {
                const newMode = [...prev];
                newMode[activeIndex] = false;
                return newMode;
              });
              setUnifySelections(prev => {
                const newSelections = [...prev];
                newSelections[activeIndex] = new Set();
                return newSelections;
              });
            }}
          >
            ✗ Cancel
          </button>
        </div>
      </div>
    )}
  </div>
)}
      {/* Enhanced Processing Modal with Real-time Preview */}
      {croppingIndex !== null && currentImageInfo && (
        <div className="crop-modal">
          <div className="enhanced-crop-layout">
            <div className="main-crop-container">
              {windowMode === 'crop' ? (
                <Cropper
                  image={tempImagePreview || topImages[croppingIndex]}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={keepSquare ? 1 : null}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onRotationChange={setRotation}
                  onCropComplete={handleCropComplete}
                  cropShape="rect"
                  showGrid={true}
                  minZoom={0.5}
                  maxZoom={3}
                  objectFit="contain"
                />
              ) : (
                <div className="sliding-window-container">
                  <img 
                    src={tempImagePreview || topImages[croppingIndex]} 
                    alt="Sliding window preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      position: 'relative',
                      transform: `translate(${previewTransformations.offsetX}px, ${previewTransformations.offsetY}px) scale(${previewTransformations.scale}) rotate(${previewTransformations.rotation}deg)`,
                      transition: 'transform 0.1s ease'
                    }}
                  />
                  <div 
                    className="sliding-window-overlay"
                    style={{
                      position: 'absolute',
                      border: '3px solid #ff0000',
                      backgroundColor: 'rgba(255, 0, 0, 0.1)',
                      left: `${(windowPosition.x / currentImageInfo.dimensions.width) * 100}%`,
                      top: `${(windowPosition.y / currentImageInfo.dimensions.height) * 100}%`,
                      width: `${(windowSize.width / currentImageInfo.dimensions.width) * 100}%`,
                      height: `${(windowSize.height / currentImageInfo.dimensions.height) * 100}%`,
                      pointerEvents: 'none'
                    }}
                  />
                </div>
              )}
            </div>
            
            <div className="enhancement-controls">
              <h3>Image Processing Controls</h3>
              
              {/* Mode Toggle */}
              <div className="control-group">
                <label>Processing Mode:</label>
                <div className="mode-buttons">
                  <button 
                    className={`mode-btn ${windowMode === 'crop' ? 'active' : ''}`}
                    onClick={() => setWindowMode('crop')}
                  >
                    Free Crop
                  </button>
                  <button 
                    className={`mode-btn ${windowMode === 'sliding' ? 'active' : ''}`}
                    onClick={() => setWindowMode('sliding')}
                  >
                    Sliding Window
                  </button>
                </div>
              </div>

              {windowMode === 'sliding' && (
                <>
                  <div className="control-group">
                    <label>Window Size</label>
                    <button 
                      className={`toggle-btn ${!customWindowSize ? 'active' : ''}`}
                      onClick={() => setCustomWindowSize(!customWindowSize)}
                    >
                      {customWindowSize ? 'Preset Sizes' : 'Custom Size'}
                    </button>
                    
                    {customWindowSize ? (
                      <div className="custom-size-inputs">
                        <div>
                          <label>Width: {windowSize.width}px</label>
                          <input
                            type="range"
                            min={50}
                            max={Math.min(currentImageInfo.dimensions.width, 2048)}
                            value={windowSize.width}
                            onChange={(e) => setWindowSize(prev => ({...prev, width: Number(e.target.value)}))}
                          />
                        </div>
                        <div>
                          <label>Height: {windowSize.height}px</label>
                          <input
                            type="range"
                            min={50}
                            max={Math.min(currentImageInfo.dimensions.height, 2048)}
                            value={windowSize.height}
                            onChange={(e) => setWindowSize(prev => ({...prev, height: Number(e.target.value)}))}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="preset-sizes">
                        {[256, 512, 1024].map(size => (
                          <button
                            key={size}
                            className={`preset-btn ${windowSize.width === size && windowSize.height === size ? 'active' : ''}`}
                            onClick={() => setWindowSize({width: size, height: size})}
                          >
                            {size}×{size}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="control-group">
                    <label>Window Position</label>
                    <div>
                      <label>X: {windowPosition.x}px</label>
                      <input
                        type="range"
                        min={0}
                        max={Math.max(0, currentImageInfo.dimensions.width - windowSize.width)}
                        value={windowPosition.x}
                        onChange={(e) => setWindowPosition(prev => ({...prev, x: Number(e.target.value)}))}
                      />
                    </div>
                    <div>
                      <label>Y: {windowPosition.y}px</label>
                      <input
                        type="range"
                        min={0}
                        max={Math.max(0, currentImageInfo.dimensions.height - windowSize.height)}
                        value={windowPosition.y}
                        onChange={(e) => setWindowPosition(prev => ({...prev, y: Number(e.target.value)}))}
                      />
                    </div>
                  </div>
                </>
              )}

              {windowMode === 'crop' && (
                <>
                  <div className="control-group">
                    <button 
                      className={`square-toggle-btn ${keepSquare ? 'active' : ''}`}
                      onClick={() => setKeepSquare(!keepSquare)}
                    >
                      {keepSquare ? 'Square Mode' : 'Free Resize'}
                    </button>
                  </div>
                  
                  <div className="control-group">
                    <label>Zoom: {zoom.toFixed(1)}x</label>
                    <input
                      type="range"
                      min={0.5}
                      max={3}
                      step={0.1}
                      value={zoom}
                      onChange={(e) => setZoom(Number(e.target.value))}
                    />
                  </div>
                </>
              )}
              
              <div className="control-group">
                <label>Rotation: {rotation}°</label>
                <input
                  type="range"
                  min={-180}
                  max={180}
                  value={rotation}
                  onChange={(e) => setRotation(Number(e.target.value))}
                />
              </div>
              
              <div className="control-group">
                <label>Scale: {scale.toFixed(2)}x</label>
                <input
                  type="range"
                  min={0.5}
                  max={3}
                  step={0.1}
                  value={scale}
                  onChange={(e) => setScale(Number(e.target.value))}
                />
              </div>

              <div className="control-group">
                <label>Offset X: {offsetX}px</label>
                <input
                  type="range"
                  min={-200}
                  max={200}
                  value={offsetX}
                  onChange={(e) => setOffsetX(Number(e.target.value))}
                />
              </div>
              
              <div className="control-group">
                <label>Offset Y: {offsetY}px</label>
                <input
                  type="range"
                  min={-200}
                  max={200}
                  value={offsetY}
                  onChange={(e) => setOffsetY(Number(e.target.value))}
                />
              </div>
              
              {/* File upload section for editing */}
              <div className="control-group">
                <label>Change Image:</label>
                <input
                  type="file"
                  accept="image/*,.tiff,.tif"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleFileChange(croppingIndex, e);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: '#444',
                    color: '#fff',
                    border: '1px solid #666',
                    borderRadius: '4px'
                  }}
                />
              </div>
              
              {currentImageInfo.metadata && (
                <div className="control-group metadata-info">
                  <label>File Info:</label>
                  <div className="metadata-display">
                    <small>Format: {currentImageInfo.format.toUpperCase()}</small>
                    {(currentImageInfo.format === 'tiff' || currentImageInfo.format === 'tif') && (
                      <small style={{color: '#FF9800'}}>🔄 Will convert to PNG for AI processing</small>
                    )}
                    {currentImageInfo.metadata.exifData?.gps && (
                      <small style={{color: '#4CAF50'}}>📍 GPS Data Preserved</small>
                    )}
                    {currentImageInfo.metadata.geoTags && Object.keys(currentImageInfo.metadata.geoTags).length > 0 && (
                      <small style={{color: '#4CAF50'}}>🗺️ GeoTIFF Tags Preserved</small>
                    )}
                    {currentImageInfo.metadata.fileInfo && (
                      <small style={{color: '#888'}}>
                        Size: {(currentImageInfo.metadata.fileInfo.size / (1024*1024)).toFixed(1)}MB
                      </small>
                    )}
                  </div>
                </div>
              )}
              
              <div className="crop-controls">
                <button 
                  className="save-crop-btn" 
                  onClick={handleCropSave}
                  disabled={isProcessing[croppingIndex]}
                >
                  {isProcessing[croppingIndex] ? 
                    (currentImageInfo.format === 'tiff' || currentImageInfo.format === 'tif' ? 
                      "Converting TIFF & Processing..." : 
                      "Processing...") : 
                    (currentImageInfo.format === 'tiff' || currentImageInfo.format === 'tif' ? 
                      "Convert to PNG & Analyze" : 
                      "Process & Analyze")
                  }
                </button>
                <button className="cancel-crop-btn" onClick={handleCropCancel}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="chart-container">
        <Line data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

export default TimeSeriesPage;